import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { setup } from '@core/api';
import { CenteredComponent, LogoutButton } from '@core/components';
import { environment, Environment } from '@core/environment';
import { useSetupGrpcTransport } from '@core/redux/transport';
import { setUser } from '@core/redux/user';
import { AuthSyntheticProvider } from '@deepup/auth-utils';
import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import { ReactElement, useEffect } from 'react';

const isCypressEnvironment = !!window.Cypress;

const env = environment as Environment;
const isDev = env.isDev;
const apiUrl = env.apiUrl;
const domain = env.auth0.domain as string;
const clientId = env.auth0.clientId as string;
const audience = env.auth0.authorizationParams.audience as string;
const scope = env.auth0.authorizationParams.scope as string;
const authDev = env.authDev;
const basePath = env.basePath;

const auth0Config = {
  domain,
  clientId,
  authorizationParams: {
    redirect_uri: basePath ? `${window.location.origin}/${basePath}` : window.location.origin,
    audience,
  },
};

const IS_DEV = import.meta.env.DEV || !!authDev;

const Auth0Container = ({ children }: { children: ReactElement }) => {
  const { isAuthenticated, isLoading, error, loginWithRedirect, getAccessTokenSilently, user } =
    useAuth0();

  // setup the grpc transport for use in redux queries
  useSetupGrpcTransport();

  useEffect(() => {
    setUser(user);
  }, [user]);

  useEffect(() => {
    if (isAuthenticated) {
      setup({
        baseUrl: apiUrl,
        getAccessToken: () =>
          getAccessTokenSilently({ authorizationParams: { audience, scope } }) as Promise<string>,
      });
    }
  }, [isAuthenticated, apiUrl, getAccessTokenSilently, audience, scope]);

  if (isCypressEnvironment) {
    // !!!CAUTION!!!
    // This hack injects the access token from cypress into the backoffice without doing the redirect dance 💃
    // It only works for api calls, which use our @core/api/api-service.
    // Fortunately all redux slices use this service and hence most of our application works with it.
    // However it WILL NOT WORK in other scopes that use getAccessTokenSilently() from the useAuth0 hook.
    // TODO: Find a generic approach that support all possible e2e tests
    //  https://docs.cypress.io/guides/testing-strategies/auth0-authentication#Adapting-the-front-end
    const accessToken = localStorage.getItem('Auth0-Cypress-AccessToken');
    if (accessToken) {
      setup({
        baseUrl: apiUrl,
        getAccessToken: () => Promise.resolve(accessToken),
      });
      return children;
    }
  }

  if (isLoading) {
    return (
      <CenteredComponent>
        <CircularProgress />
      </CenteredComponent>
    );
  }

  if (!isAuthenticated && error) {
    return (
      <CenteredComponent>
        <Alert severity="error">{error.message}</Alert>
        <Stack direction="row" alignItems="center">
          <Typography color="white">Please </Typography>
          <LogoutButton />
          <Typography color="white">
            , make sure to have the correct permission set and try again
          </Typography>
        </Stack>
      </CenteredComponent>
    );
  }

  if (!isAuthenticated) {
    loginWithRedirect().catch((e) => isDev && console.error(e));
    return (
      <CenteredComponent>
        <CircularProgress />
      </CenteredComponent>
    );
  }

  return children;
};

const Auth0ProviderWithConfig = ({ children }: { children: ReactElement }) => (
  <Auth0Provider
    domain={auth0Config.domain}
    clientId={auth0Config.clientId}
    authorizationParams={auth0Config.authorizationParams}
  >
    {children}
  </Auth0Provider>
);

const Auth0Boundary = ({ children }: { children: ReactElement }) => {
  if (window.Cypress) {
    return <Auth0ProviderWithConfig>{children}</Auth0ProviderWithConfig>;
  }

  if (IS_DEV) {
    return <AuthSyntheticProvider auth0Config={auth0Config}>{children}</AuthSyntheticProvider>;
  }

  return <Auth0ProviderWithConfig>{children}</Auth0ProviderWithConfig>;
};

export const Authentication = ({ children }: { children: ReactElement }) => (
  <Auth0Boundary>
    <Auth0Container>{children}</Auth0Container>
  </Auth0Boundary>
);
